import { asGlobalResourceQuery } from 'v5/api/_common/query/workspace-scoping';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import type { ApiClient } from '../../../lib/api/ApiClient.types';
import type { FieldNameTypeMapping } from '../../services/internal/MetastructureMappingService/MetastructureMappingService';
import { paginationUtils } from '../_common/query/pagination';
import { getResourceListRequest } from '../resource/resource.api.http';
import type { ResourceSearchQuery } from '../resource/resource.api';
import {
  type BoardRef,
  type ShortlistBlockPage,
  type ShortlistBlockQuery,
  type ShortlistBoardApi,
  ShortlistBoardError
} from './shortlistBoard.api';
import {
  blockPage$,
  blockPins$,
  blockPinsFromHttp,
  blockCardFromHttp,
  PREVIEW_PINS_LIMIT,
  boardSummary$,
  clientSummary$,
  clientSummaryFromHttp,
  clientViewToHttp,
  shortlistSummaryFromHttp
} from './shortlistBoard.http.schema';

/** The contact-search part (`query`, `filters`) of a request, in the search endpoint's format. */
function searchPart(
  fieldMappings: FieldNameTypeMapping,
  search: string,
  filters: ResourceSearchQuery['filters']
): { query: string; filters: Record<string, unknown> } {
  const request = getResourceListRequest(
    asGlobalResourceQuery({
      search,
      filters,
      sort: null,
      page: paginationUtils.ofDefault()
    }),
    fieldMappings
  );
  const requestFilters = request.filters;
  return {
    query: typeof request.query === 'string' ? request.query : '',
    filters:
      typeof requestFilters === 'object' && requestFilters !== null
        ? Object.fromEntries(
            Object.entries(requestFilters).filter(
              ([, value]) => value !== undefined
            )
          )
        : {}
  };
}

function blockQueryBody(
  fieldMappings: FieldNameTypeMapping,
  query: ShortlistBlockQuery
) {
  return {
    ...searchPart(fieldMappings, query.search, query.filters),
    order_by: {
      field: query.sort.field,
      direction: query.sort.direction === 'desc' ? 'DESC' : 'ASC'
    },
    has_comments: query.hasComments
  };
}

function parsePage(
  response: unknown,
  previewPins: boolean
): ShortlistBlockPage {
  const parsed = parseWithDecoratedError(blockPage$, response);
  return {
    blocks: parsed.results.map(card => blockCardFromHttp(card, previewPins)),
    statuses: parsed.statuses ?? [],
    total: parsed.paging.total
  };
}

/**
 * The server refuses a shortlist asked for under another workspace or team with a 409 whose body
 * says `reason: 'out_of_scope'` (`{ status, error: { code, message, reason, ... } }`).
 */
function boardErrorOf(error: unknown): unknown {
  const details =
    typeof error === 'object' && error !== null && 'error' in error
      ? error.error
      : null;
  return typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    error.status === 409 &&
    typeof details === 'object' &&
    details !== null &&
    'reason' in details &&
    details.reason === 'out_of_scope'
    ? new ShortlistBoardError('belongs-elsewhere', { cause: error })
    : error;
}

export interface ShortlistBoardHttpConfig {
  client: ApiClient;
  /** The session's token goes along when there is one: it makes the reader a member. */
  isSignedIn: () => boolean;
  /** The guest id this browser keeps, if any: where feedback is private, it picks out the guest's own. */
  getGuestId: () => string | null;
}

/** How a board request names its reader: the session, the guest's password and their id. */
export function boardRequestOptions(
  config: Omit<ShortlistBoardHttpConfig, 'client'>,
  password: string | null
) {
  const guestId = config.getGuestId();
  return {
    // a 401 here asks for the shortlist's password; the session is fine
    keepsSessionOn401: true,
    token: config.isSignedIn(),
    headers: {
      ...(password ? { 'x-shortlist-password': password } : {}),
      ...(guestId ? { 'x-shortlist-guest-id': guestId } : {})
    }
  };
}

export const shortlistBoardPath = (publicId: string) =>
  `/shortlist-boards/${encodeURIComponent(publicId)}`;

export function createShortlistBoardApi(
  config: ShortlistBoardHttpConfig
): ShortlistBoardApi {
  const { client } = config;
  // Edit's summary and writes are a member's: they go with the token; a read names its reader
  const read = (ref: BoardRef) => ({
    ...boardRequestOptions(config, ref.password),
    params: { view: ref.view }
  });

  return {
    getBoardSummary: async (shortlist, scope) => {
      const response = await client
        .get(`${shortlistBoardPath(String(shortlist))}/summary`, {
          token: true,
          params: {
            view: 'board',
            // the server checks the scope only with a team: '' would read as team 0
            ...(scope?.teamId != null && {
              enterprise_team_id: scope.teamId,
              ...(scope.workspaceId != null && {
                enterprise_workspace_id: scope.workspaceId
              })
            })
          }
        })
        .catch(error => {
          throw boardErrorOf(error);
        });
      const summary = parseWithDecoratedError(boardSummary$, response);
      return shortlistSummaryFromHttp(summary.shortlist, summary.qr);
    },

    getClientSummary: async (publicId, password) =>
      clientSummaryFromHttp(
        parseWithDecoratedError(
          clientSummary$,
          await client.get(
            `${shortlistBoardPath(publicId)}/summary`,
            read({ publicId, view: 'client', password })
          )
        )
      ),

    // no `p` or `count`: the server answers with every block the query matches
    getAllBlocks: async (ref, fieldMappings, query, previewPins, signal) =>
      parsePage(
        await client.post(`${shortlistBoardPath(ref.publicId)}/blocks/search`, {
          ...read(ref),
          params: {
            ...read(ref).params,
            ...(previewPins && {
              expand: 'pins',
              pins_limit: PREVIEW_PINS_LIMIT
            })
          },
          data: blockQueryBody(fieldMappings, query),
          signal
        }),
        previewPins
      ),

    addContacts: async (publicId, fieldMappings, request) => {
      const data = {
        ...(request.type === 'contacts'
          ? { contact_ids: request.contactIds }
          : {
              search: searchPart(
                fieldMappings,
                request.query.search,
                request.query.filters
              )
            }),
        ...(request.position && {
          position: {
            before_id: request.position.beforeId,
            after_id: request.position.afterId
          }
        })
      };
      const response = await client.post<{
        added_block_ids: number[];
        reactivated_block_ids: number[];
      }>(`${shortlistBoardPath(publicId)}/blocks/add`, { token: true, data });
      return {
        addedBlockIds: response.added_block_ids,
        reactivatedBlockIds: response.reactivated_block_ids
      };
    },

    removeBlocks: async (publicId, fieldMappings, request) => {
      const data =
        request.type === 'blocks'
          ? { block_ids: request.blockIds }
          : { search: blockQueryBody(fieldMappings, request.query) };
      const response = await client.post<{ removed_block_ids: number[] }>(
        `${shortlistBoardPath(publicId)}/blocks/remove`,
        { token: true, data }
      );
      return { removedBlockIds: response.removed_block_ids };
    },

    moveBlocks: async (publicId, request) =>
      client.post(`${shortlistBoardPath(publicId)}/blocks/move`, {
        token: true,
        data: {
          block_ids: request.blockIds,
          before_id: request.beforeId,
          after_id: request.afterId
        }
      }),

    getBlockPins: async (ref, blockId) => {
      const response = await client.get(
        `${shortlistBoardPath(ref.publicId)}/blocks/${blockId}/pins`,
        read(ref)
      );
      return blockPinsFromHttp(
        parseWithDecoratedError(blockPins$, response).results
      );
    },

    updateShortlist: async (id, { clientView, ...patch }) => {
      await client.put(`/shortlists/${id}`, {
        token: true,
        data: {
          ...patch,
          ...(clientView === undefined ? {} : clientViewToHttp(clientView))
        }
      });
    },

    updateBlockNote: async (shortlistId, blockId, note) => {
      await client.put(
        `/shortlists/${shortlistId}/associations/shortlist_blocks/${blockId}`,
        {
          token: true,
          data: { data: { paragraph: note } }
        }
      );
    },

    updateBlockPins: async (shortlistId, blockId, pins) => {
      await client.put(
        `/shortlists/${shortlistId}/associations/shortlist_blocks/${blockId}`,
        {
          token: true,
          data: {
            pins: pins.map((pin, order) => ({
              ...(pin.id === null ? {} : { id: pin.id }),
              ...(pin.fileId === null ? {} : { file_id: pin.fileId }),
              pin_type: pin.pinType,
              url: pin.url,
              order
            }))
          }
        }
      );
    }
  };
}
